import '../../styles/EditCase.css';
import SeverityBadge from './SeverityBadge';
import ReviewStamp from './ReviewStamp';

const SEVERITY_RANK = { critical: 0, high: 1, medium: 2, info: 3 };

// The identity flag (F1) carries per-document values that may be null.
function notStated(value) {
    if (value === null || value === undefined || value === '') return 'Not stated';
    return String(value);
}

// Sorted for display, but each entry keeps its stored index: EditCase
// writes redFlags.flags by stored position, so a display index would
// review a different flag whenever the stored order is not by severity.
function sortBySeverity(flags) {
    return flags
        .map((flag, index) => ({ flag, index }))
        .sort((a, b) => {
            const ra = SEVERITY_RANK[a.flag.severity] ?? 99;
            const rb = SEVERITY_RANK[b.flag.severity] ?? 99;
            return ra - rb;
        });
}

export default function RedFlagsAlert({ redFlags, onRedFlagReview, onMarkAllReviewed }) {
    if (!redFlags) return null;

    if (redFlags.summary?.has_red_flags) {
        const entries = sortBySeverity(redFlags.flags);
        const flags = entries.map(e => e.flag);
        const reviewedCount = flags.filter(f => f.status === 'approved' || f.status === 'rejected').length;
        const allReviewed = reviewedCount === flags.length;
        const hasPending = reviewedCount < flags.length;

        return (
            <div className={`red-flags-alert${allReviewed ? ' red-flags-alert--all-reviewed' : ''}`}>
                <div className="red-flags-header">
                    <span className="red-flags-icon">&#9888;</span>
                    <span className="red-flags-title">
                        {redFlags.summary.total_detected} Red Flag{redFlags.summary.total_detected !== 1 ? 's' : ''} Detected
                    </span>
                    <span className="red-flags-review-count">
                        {reviewedCount} of {flags.length} reviewed
                    </span>
                    {hasPending && onMarkAllReviewed && (
                        <button className="red-flags-mark-all" onClick={onMarkAllReviewed}>
                            Mark all reviewed
                        </button>
                    )}
                </div>
                <div className="red-flags-list">
                    {entries.map(({ flag, index }) => {
                        const status = flag.status || 'pending';
                        return (
                            <div key={index} className={`red-flag-item red-flag-item--${status}`}>
                                <div className="red-flag-category">{flag.category}</div>
                                {flag.severity && <SeverityBadge severity={flag.severity} />}
                                <div className="red-flag-name">{flag.flag}</div>
                                {flag.evidence && (
                                    <div className="red-flag-evidence">{flag.evidence}</div>
                                )}
                                {Array.isArray(flag.details?.documents) && flag.details.documents.length > 0 && (
                                    <ul className="red-flag-documents">
                                        {flag.details.documents.map((d, i) => (
                                            <li key={`${d?.document ?? 'doc'}-${i}`}>
                                                {`${notStated(d?.document)}: ${notStated(d?.name)}, DOB ${notStated(d?.dob)}, MRN ${notStated(d?.mrn)}`}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {(status === 'approved' || status === 'rejected') && (
                                    <ReviewStamp review={flag.review} />
                                )}
                                {onRedFlagReview && (
                                    <div className="red-flag-actions">
                                        <button
                                            className={`red-flag-btn red-flag-btn--approve${status === 'approved' ? ' active' : ''}`}
                                            onClick={() => onRedFlagReview(index, 'approved')}
                                        >
                                            &#10003; Correct
                                        </button>
                                        <button
                                            className={`red-flag-btn red-flag-btn--dismiss${status === 'rejected' ? ' active' : ''}`}
                                            onClick={() => onRedFlagReview(index, 'rejected')}
                                        >
                                            &#10005; Dismiss
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    return (
        <div className="red-flags-clear">
            <span className="red-flags-clear-icon">&#10003;</span>
            No red flags detected
        </div>
    );
}
